import type { InvoiceData } from '@subscriptions/domain/entities';
import { createInvoiceData } from '@subscriptions/domain/entities';
import { redactRfc } from '@subscriptions/domain/value-objects';
import type { FiscalRegimesDto, RetryInvoiceDataDto, RetryInvoiceScopeDto } from '@subscriptions/application/dtos';
import type {
  InvoiceDataApiResponse,
  CreateInvoiceDataApiRequest,
  UpdateInvoiceDataApiRequest,
  FiscalRegimesApiResponse,
  InvoiceActionApiQuery,
  InvoiceActionApiResponse,
} from '@subscriptions/infrastructure/api/types';

/** Mapper type for invoice data API transformations */
export type InvoiceDataApiMapperType = {
  mapApiResponseToEntity: (response: InvoiceDataApiResponse) => InvoiceData;
  mapEntityToCreateApiRequest: (invoiceData: InvoiceData) => CreateInvoiceDataApiRequest;
  mapEntityToUpdateApiRequest: (invoiceData: InvoiceData) => UpdateInvoiceDataApiRequest;
  mapFiscalRegimesApiResponseToDto: (response: FiscalRegimesApiResponse) => FiscalRegimesDto;
  mapRetryScopeToApiQuery: (scope: RetryInvoiceScopeDto) => InvoiceActionApiQuery;
  mapInvoiceActionApiResponseToDto: (response: InvoiceActionApiResponse) => RetryInvoiceDataDto;
};

/**
 * Creates mapper for invoice data API type conversions.
 *
 * Translates backend field names to domain vocabulary:
 * - API `taxId` ↔ domain `rfc`
 * - API `taxInvoiceUse` ↔ domain `cfdi`
 * - API `taxRegime` is a plain SAT code string in both directions
 *
 * Fiscal regimes still come from the legacy endpoint and keep their
 * original `{data: {...}}` shape.
 */
export const createInvoiceDataApiMapper = (): InvoiceDataApiMapperType => {
  // Read flow: soft validation so one corrupt record degrades to empty fields instead of breaking the list
  const mapApiResponseToEntity = (response: InvoiceDataApiResponse): InvoiceData =>
    createInvoiceData(
      {
        uuid: response.uuid,
        orgUuid: response.orgUuid,
        businessName: response.businessName ?? '',
        firstName: response.firstName ?? '',
        firstLastName: response.firstLastName ?? '',
        secondLastName: response.secondLastName ?? '',
        postalCode: response.postalCode,
        rfc: response.taxId,
        cfdi: response.taxInvoiceUse,
        email: response.email,
        taxRegime: response.taxRegime,
      },
      { softValidation: true },
    );

  const mapEntityToCreateApiRequest = (invoiceData: InvoiceData): CreateInvoiceDataApiRequest => ({
    businessName: invoiceData.businessName || null,
    firstName: invoiceData.firstName || null,
    firstLastName: invoiceData.firstLastName || null,
    secondLastName: invoiceData.secondLastName || null,
    postalCode: invoiceData.postalCode,
    taxId: invoiceData.rfc,
    taxInvoiceUse: invoiceData.cfdi,
    email: invoiceData.email,
    taxRegime: invoiceData.taxRegime,
  });

  const mapEntityToUpdateApiRequest = (invoiceData: InvoiceData): UpdateInvoiceDataApiRequest =>
    mapEntityToCreateApiRequest(invoiceData);

  const mapFiscalRegimesApiResponseToDto = (response: FiscalRegimesApiResponse): FiscalRegimesDto => ({
    taxRegimeCompany: response.data.taxRegimeCompany,
    taxRegimePersons: response.data.taxRegimePersons,
  });

  // The scope of the retry is the endpoint's only input, and it travels in the query string.
  const mapRetryScopeToApiQuery = (scope: RetryInvoiceScopeDto): InvoiceActionApiQuery => ({
    only_failed: scope.onlyFailed,
    ...(scope.includeDeleted && { include_deleted: true }),
  });

  // reinvoice-sync is the only endpoint of this domain answering in snake_case. Mapped field by
  // field with no fallbacks: a default would quietly turn a contract mismatch into a plausible
  // object, which is exactly how a failed re-invoicing once got read as a success.
  const mapInvoiceActionApiResponseToDto = (response: InvoiceActionApiResponse): RetryInvoiceDataDto => ({
    paymentUuid: response.payment_uuid,
    priorInvoices: response.prior_invoices,
    canceled: response.canceled,
    deleted: response.deleted,
    invoices: response.invoices.map((invoice) => ({
      uuid: invoice.uuid,
      storeUuid: invoice.store_uuid,
      status: invoice.status,
      fiscalFolio: invoice.fiscal_folio,
      // Redacted here too: the PAC quotes the RFC and this reason ends up in a toast (Charter §5.1).
      errorMessage: invoice.error_message ? redactRfc(invoice.error_message) : null,
    })),
  });

  return {
    mapApiResponseToEntity,
    mapEntityToCreateApiRequest,
    mapEntityToUpdateApiRequest,
    mapFiscalRegimesApiResponseToDto,
    mapRetryScopeToApiQuery,
    mapInvoiceActionApiResponseToDto,
  };
};
